import { useCallback, useEffect, useMemo, useState } from 'react'
import { ColDef } from 'ag-grid-community'
import { Box, styled } from '@mui/material'
import { Flag as FlagIcon, Route as RouteIcon } from '@mui/icons-material'
import MilestoneDialog from './MilestoneDialog'
import SingleMilestoneDialog from './SingleMilestoneDialog'
import { QuickFilterKeys } from '../..'
import { intl } from '../../../../../i18n'
import { ColumnFilterButtonGroup } from '../../../../components/buttons/ColumnFilterButtonGroup'
import {
  AggregateFieldToggleGroup,
  AggregateTargetToggleGroup,
  WorkloadUnitToggleGroup,
} from '../../../../components/toggleGroups'
import DisplayFormatButton from '../../../../containers/BulkSheetView/components/header/DisplayFormatButton'
import ColumnSettingButton from '../../../../containers/BulkSheetView/components/header/ColumnSettingButton'
import FavoriteColumnFilterButton from '../../../../containers/BulkSheetView/components/header/FavoriteColumnFilterButton'
import FilterButton from '../../../../containers/BulkSheetView/components/header/FilterButton'
import GanttChartParameter from '../../../../containers/BulkSheetView/components/header/GanttChartParameter'
import SortButton from '../../../../containers/BulkSheetView/components/header/SortButton'
import ProgressFilter from './ProgressFilter'
import FileImportButton from '../../../../containers/BulkSheetView/components/header/FileImportButton'
import FileExportButton from '../../../../containers/BulkSheetView/components/header/FileExportButton'
import {
  AggregateField,
  AggregateTarget,
} from '../../../../../domain/entity/WbsItemEntity'
import { WorkloadUnit } from '../../../../../lib/functions/workload'
import { Button } from '../../../../components/buttons'
import { colorPalette } from '../../../../style/colorPalette'
import { SortedColumnState } from '../../../../model/bulkSheetColumnSortState'
import ReportIcon from '../../../../components/icons/ReportIcon'
import { BootstrapTooltip } from '../../../../components/tooltips/Tooltip/BootStrapToopTip'
import ReloadButton from '../../../../containers/BulkSheetView/components/header/ReloadButton'
import { AiChat } from '../AiChat'
import {
  ColumnQuickFilterKey,
  ProjectPlanNewRow,
  ProjectPlanRowBody,
  SelectedColumnQuickFilterKeyType,
} from '../../projectPlanNew'
import { MilestoneDetail } from '../../../../../domain/entity/MilestoneEntity'
import { isAiAvailable } from '../../../../../lib/functions/ai'
import { featureFlagService } from '../../../../../utils/feature-flag'

const isGanttV2 = featureFlagService.isEnabled('gantt-v2')

type Props = {
  onChangeAggregateTarget: (value: AggregateTarget) => void
  aggregateTarget: AggregateTarget
  onChangeAggregateField: (value: AggregateField) => void
  aggregateField: AggregateField
  onChangeWorkloadUnit: (_: WorkloadUnit) => void
  workloadUnit: WorkloadUnit
  quickFilters: QuickFilterKeys | undefined
  onChangeQuickFilters: (
    key: QuickFilterKeys | undefined
  ) => void | Promise<void>
  onChangeColumnFilter: (value: ColumnQuickFilterKey) => void
  onSwitchingGanttChart: (value: boolean) => void
  gantt: boolean
  rowHeight: number | undefined
  onChangeHeight: (value: number) => void
  isRowHeightFixed?: boolean
  onClickImportExcel: ((data: Uint8Array) => void | Promise<any>) | undefined
  onClickExportExcel: () => void
  filteredColumns: ColDef[]
  onDeleteFilteredColumn?: (column: ColDef) => void
  resetFilters: () => void
  onDeleteSortedColumn?: ((column: string | ColDef<any>) => void) | undefined
  onDeleteSortedAllColumns: () => void
  onChangeSortColumnState?:
    | ((column: string | ColDef<any>, sort: 'asc' | 'desc' | null) => void)
    | undefined
  onReload: () => void
  onClickColumnSettingButton: (e: React.MouseEvent) => void
  onClickFavoriteColumnFilterButton: () => void
  sortColumnsState: SortedColumnState[]
  currentFormat?: string | undefined
  onChangeDateFormat: (value: string) => void
  onChangeOpenReport: (_: boolean) => void
  openReport: boolean
  rootWbsItem?: string | undefined
  columnSettingOpen: boolean
  data: ProjectPlanNewRow[]
  setData: (data: ProjectPlanNewRow[]) => void
  setProjectPlanBody: (
    row: ProjectPlanNewRow,
    body: ProjectPlanRowBody
  ) => ProjectPlanNewRow
  selectedColumnQuickFilterKey: SelectedColumnQuickFilterKeyType
  milestones?: MilestoneDetail[]
  isLoadingMilestones?: boolean
  onRefreshMilestones?: () => void
  isMilestoneEnabled?: boolean
  onRegisterMilestoneCallbacks?: (callbacks: {
    openWithDate: (date: string) => void
    openWithUuid: (uuid: string) => void
  }) => void
  isDependencyCriticalPathEnabled?: boolean
  onOpenCriticalPathDialog?: () => void
  rootScheduledStartDate?: string
  rootScheduledEndDate?: string
}

const ProjectPlanNewWbsHeader = ({
  aggregateTarget,
  onChangeAggregateTarget,
  onChangeAggregateField,
  aggregateField,
  onChangeWorkloadUnit,
  workloadUnit,
  quickFilters,
  onChangeQuickFilters,
  onChangeColumnFilter,
  onSwitchingGanttChart,
  rowHeight,
  gantt,
  onChangeHeight,
  isRowHeightFixed,
  onClickImportExcel,
  onClickExportExcel,
  filteredColumns,
  onDeleteFilteredColumn,
  resetFilters,
  onDeleteSortedColumn,
  onDeleteSortedAllColumns,
  onChangeSortColumnState,
  onReload,
  onClickColumnSettingButton,
  onClickFavoriteColumnFilterButton,
  sortColumnsState,
  currentFormat,
  onChangeDateFormat,
  onChangeOpenReport,
  openReport,
  rootWbsItem,
  columnSettingOpen,
  data,
  setData,
  setProjectPlanBody,
  selectedColumnQuickFilterKey,
  milestones,
  isLoadingMilestones,
  onRefreshMilestones,
  isMilestoneEnabled,
  onRegisterMilestoneCallbacks,
  isDependencyCriticalPathEnabled,
  onOpenCriticalPathDialog,
  rootScheduledStartDate,
  rootScheduledEndDate,
}: Props) => {
  const [milestoneDialogOpen, setMilestoneDialogOpen] = useState(false)
  const [initialAddDate, setInitialAddDate] = useState<string | undefined>()
  const [initialSelectedUuid, setInitialSelectedUuid] = useState<
    string | undefined
  >()
  const [singleMilestoneDialogOpen, setSingleMilestoneDialogOpen] =
    useState(false)
  const [editingMilestone, setEditingMilestone] = useState<
    MilestoneDetail | undefined
  >()

  const handleOpenMilestoneDialog = useCallback(() => {
    setInitialSelectedUuid(undefined)
    setMilestoneDialogOpen(true)
  }, [])

  const handleOpenAddMilestoneDialogWithDate = useCallback((date: string) => {
    setInitialAddDate(date)
    setEditingMilestone(undefined)
    setSingleMilestoneDialogOpen(true)
  }, [])

  const handleOpenEditMilestoneDialog = useCallback(
    (uuid: string) => {
      const milestone = milestones?.find(m => m.uuid === uuid)
      if (milestone) {
        setEditingMilestone(milestone)
        setInitialAddDate(undefined)
        setSingleMilestoneDialogOpen(true)
      }
    },
    [milestones]
  )

  const handleCloseMilestoneDialog = useCallback(() => {
    setMilestoneDialogOpen(false)
    setInitialSelectedUuid(undefined)
  }, [])

  const handleCloseSingleMilestoneDialog = useCallback(() => {
    setSingleMilestoneDialogOpen(false)
    setInitialAddDate(undefined)
    setEditingMilestone(undefined)
  }, [])

  useEffect(() => {
    if (onRegisterMilestoneCallbacks) {
      onRegisterMilestoneCallbacks({
        openWithDate: handleOpenAddMilestoneDialogWithDate,
        openWithUuid: handleOpenEditMilestoneDialog,
      })
    }
  }, [
    onRegisterMilestoneCallbacks,
    handleOpenAddMilestoneDialogWithDate,
    handleOpenEditMilestoneDialog,
  ])

  const displayAiChat = useMemo(() => {
    return isAiAvailable()
  }, [])
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        margin: !rootWbsItem ? '10px 10px 5px 10px' : '15px 10px 10px 10px',
        overflowX: 'auto',
        overflowY: 'hidden',
        minHeight: '47px',
      }}
    >
      <ButtonsArea>
        <ReloadButton onReload={onReload} />
        <BootstrapTooltip
          arrow={true}
          title={
            openReport
              ? intl.formatMessage({ id: 'wbs.header.close.report' })
              : intl.formatMessage({ id: 'wbs.header.open.report' })
          }
        >
          <Button
            onClick={() => onChangeOpenReport(!openReport)}
            sx={{
              background: colorPalette.monotone[2],
              borderRadius: '50%',
              height: '28px',
              width: '28px',
              marginRight: '10px',
              padding: '5px 8px',
              cursor: 'pointer',
              '&:hover': { background: colorPalette.monotone[2] },
            }}
          >
            <ReportIcon />
          </Button>
        </BootstrapTooltip>
      </ButtonsArea>
      <AggregateTargetToggleGroup
        value={aggregateTarget}
        onChange={onChangeAggregateTarget}
      />
      <AggregateFieldToggleGroup
        value={aggregateField}
        onChange={onChangeAggregateField}
      />
      <WorkloadUnitToggleGroup
        value={workloadUnit}
        refresh={onChangeWorkloadUnit}
      />
      <ProgressFilter
        selectedFilterGroups={quickFilters}
        onChangeQuickFilters={onChangeQuickFilters}
      />
      <FilterButton
        filteredColumns={filteredColumns}
        onDeleteFilteredColumn={onDeleteFilteredColumn}
        resetFilters={resetFilters}
      />
      <SortButton
        onDeleteSortedColumn={onDeleteSortedColumn}
        onDeleteSortedAllColumns={onDeleteSortedAllColumns}
        onChangeSortColumnState={onChangeSortColumnState}
        sortColumnsState={sortColumnsState}
      />
      <ColumnSettingButton
        onClick={onClickColumnSettingButton}
        open={columnSettingOpen}
      />
      <ColumnFilterButtonGroup
        options={[
          {
            label: intl.formatMessage({
              id: 'bulksheet.toolbar.columnQuickFilter.label.planning',
            }),
            value: ColumnQuickFilterKey.PLANNING,
          },
        ]}
        selectedValue={selectedColumnQuickFilterKey}
        onChange={value => onChangeColumnFilter(value as ColumnQuickFilterKey)}
      />
      <ColumnFilterButtonGroup
        options={[
          {
            label: intl.formatMessage({
              id: 'bulksheet.toolbar.columnQuickFilter.label.workResult',
            }),
            value: ColumnQuickFilterKey.WORK_RESULT,
          },
        ]}
        selectedValue={selectedColumnQuickFilterKey}
        onChange={value => onChangeColumnFilter(value as ColumnQuickFilterKey)}
      />
      <ColumnFilterButtonGroup
        options={[
          {
            label: intl.formatMessage({
              id: 'bulksheet.toolbar.columnQuickFilter.label.progress',
            }),
            value: ColumnQuickFilterKey.PROGRESS,
          },
        ]}
        selectedValue={selectedColumnQuickFilterKey}
        onChange={value => onChangeColumnFilter(value as ColumnQuickFilterKey)}
      />
      <ColumnFilterButtonGroup
        options={[
          {
            label: intl.formatMessage({
              id: 'bulksheet.toolbar.columnQuickFilter.label.productivity' + '',
            }),
            value: ColumnQuickFilterKey.PRODUCTIVITY,
          },
        ]}
        selectedValue={selectedColumnQuickFilterKey}
        onChange={value => onChangeColumnFilter(value as ColumnQuickFilterKey)}
      />
      <FavoriteColumnFilterButton onClick={onClickFavoriteColumnFilterButton} />
      <DisplayFormatButton
        height={rowHeight}
        onChangeHeight={onChangeHeight}
        isRowHeightFixed={isRowHeightFixed}
        currentFormat={currentFormat}
        onChangeDateFormat={onChangeDateFormat}
      />
      <GanttChartParameter
        onSwitchingGanttChart={onSwitchingGanttChart}
        value={gantt}
        rootScheduledStartDate={rootScheduledStartDate}
        rootScheduledEndDate={rootScheduledEndDate}
        {...(isGanttV2 && {
          isMilestoneEnabled,
          onOpenMilestoneDialog: handleOpenMilestoneDialog,
          isDependencyCriticalPathEnabled,
          onOpenCriticalPathDialog,
        })}
      />

      {!isGanttV2 && isMilestoneEnabled && (
        <BootstrapTooltip
          arrow={true}
          title={intl.formatMessage({ id: 'milestone.dialog.title' })}
        >
          <MilestoneButton
            onClick={handleOpenMilestoneDialog}
            colorPattern="skyBlue"
            variant="outlined"
          >
            <FlagIcon sx={{ fontSize: 16 }} />
          </MilestoneButton>
        </BootstrapTooltip>
      )}

      {isMilestoneEnabled && (
        <>
          <MilestoneDialog
            open={milestoneDialogOpen}
            onClose={handleCloseMilestoneDialog}
            initialMilestones={milestones}
            initialSelectedUuid={initialSelectedUuid}
            isLoadingMilestones={isLoadingMilestones}
            onSaveComplete={onRefreshMilestones}
          />

          <SingleMilestoneDialog
            open={singleMilestoneDialogOpen}
            onClose={handleCloseSingleMilestoneDialog}
            onSaveComplete={onRefreshMilestones}
            initialDate={initialAddDate}
            milestone={editingMilestone}
            milestones={milestones}
          />
        </>
      )}

      {!isGanttV2 && isDependencyCriticalPathEnabled && (
        <BootstrapTooltip
          arrow={true}
          title={intl.formatMessage({
            id: 'wbsItemLink.criticalPath.dialog.title',
          })}
        >
          <ToolbarIconButton
            onClick={onOpenCriticalPathDialog}
            colorPattern="pink"
            variant="outlined"
          >
            <RouteIcon sx={{ fontSize: 16 }} />
          </ToolbarIconButton>
        </BootstrapTooltip>
      )}

      <Box
        sx={{
          marginLeft: '10px',
          display: 'flex',
          flexDirection: 'row',
          gap: '5px',
        }}
      >
        <FileImportButton
          workloadUnit={workloadUnit}
          onClickImport={onClickImportExcel}
        />
        <FileExportButton onClickExport={onClickExportExcel} />
      </Box>
      {displayAiChat && (
        <AiChat
          data={data}
          setData={setData}
          setProjectPlanBody={setProjectPlanBody}
        />
      )}
    </div>
  )
}

const ButtonsArea = styled('div')({
  display: 'flex',
  flexDirection: 'row',
  gap: '10px',
})

const MilestoneButton = styled(Button)({
  borderRadius: '50%',
  height: '28px',
  width: '28px',
  minWidth: '28px',
  padding: '6px',
  marginLeft: '10px',
})

const ToolbarIconButton = styled(Button)({
  borderRadius: '50%',
  height: '28px',
  width: '28px',
  minWidth: '28px',
  padding: '6px',
  marginLeft: '10px',
})

export default ProjectPlanNewWbsHeader
